import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useTranslation } from "react-i18next";
import { Button } from "@carbon/react";
import i18next from "../../i18n";
import { useFileInputUploader } from "../../hooks";
import Loader from "../UI/Loader/Loader";
import PageHeader from "../carbon/PageHeader/PageHeader";
import Form from "../carbon/Form/Form";
import ConfirmDialog from "../carbon/ConfirmDialog/ConfirmDialog";
import { DeliveryTime } from "../../types/deliveries.type";
import { OfferAwardedForDeliveryResponse, UserResponse } from "../../types";
import { useCreateDelivery } from "../../hooks/useDeliveries";
import { useSites } from "../../hooks/useSites";
import { useAwardedSuppliersByOrderId } from "../../hooks/useOrders";
import { useOffersAwardedForDeliveries } from "../../hooks/useOffers";
import { useNotification, useUser } from "../../context";
import DeliveryFormFields from "./NewDelivery/DeliveryFormFields";
import DeliverySupplierPicker from "./NewDelivery/DeliverySupplierPicker";
import { buildDeliveryPayload } from "./NewDelivery/buildDeliveryPayload";
import {
  allowedExtensions,
  deliveryValidationSchema,
  manualDeliveryValidationSchema,
  NewDeliveryFormValues,
} from "./NewDelivery/newDeliveryValidationSchema";
import "./NewDelivery/newDeliveryForm.scss";

/**
 * Create-delivery form (`POST /deliveries`), migrated to Carbon + React-Hook-Form/Yup (Task 6). The
 * field body, extracted Yup schema and byte-equivalent payload builder live under `NewDelivery/`;
 * this is the orchestrator — state, data hooks, supplier-selection view, confirm step and the three
 * create paths (single manual, single selected-supplier, one-per-supplier loop).
 *
 * Confirm flow is deliberately the pre-Carbon one (NOT `NewSiteForm`'s validate-first): the page
 * button opens `ConfirmDialog` WITHOUT validating; the dialog's own confirm button runs
 * `handleSubmit(onSubmit)`, so errors surface inside the open dialog and an empty submit never
 * navigates — the exact behaviour the `/deliveries/new` e2e gating test pins.
 */
const NewDeliveryForm = () => {
  const { user } = useUser();
  const { openNotification, openError } = useNotification();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [open, setOpen] = useState(false);
  const [fileName, setFileName] = useState<string | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const orderId = searchParams.get("orderId") || "";

  const [selectedOrderId, setSelectedOrderId] = useState(orderId);
  const [isCreatingMultipleDeliveries, setIsCreatingMultipleDeliveries] =
    useState(false);
  const [currentSupplierIndex, setCurrentSupplierIndex] = useState(0);
  const [showForm, setShowForm] = useState(false);
  const [selectedSupplier, setSelectedSupplier] =
    useState<OfferAwardedForDeliveryResponse | null>(null);

  const { data: awardedSuppliers, isLoading: isLoadingAwardedSuppliers } =
    useAwardedSuppliersByOrderId(selectedOrderId);

  const {
    data: offersAwardedForDeliveries,
    isLoading: isLoadingOffersAwardedForDeliveries,
  } = useOffersAwardedForDeliveries();

  const {
    control,
    register,
    formState: { errors },
    handleSubmit,
    setValue,
    getValues,
    trigger,
  } = useForm<NewDeliveryFormValues>({
    resolver: yupResolver(
      selectedSupplier
        ? deliveryValidationSchema
        : manualDeliveryValidationSchema
    ),
    defaultValues: {
      orderName: "",
      purchaseOrderNumber: "",
      supplierEmails: [],
      receiverEmail: "",
      deliveryDate: "",
      siteId: "",
      deliveryTime: "" as DeliveryTime,
      purchaseOrderFile: "",
      comment: "",
    },
    mode: "onChange",
  });

  const {
    data: uploadedSpecificationFileUrl,
    mutate: uploadFile,
    isPending: isUploading,
  } = useFileInputUploader();

  useEffect(() => {
    if (selectedSupplier) {
      setValue("supplierEmails", [selectedSupplier.supplier.email]);
      if (
        selectedSupplier.offers &&
        selectedSupplier.offers.length > 0 &&
        selectedSupplier.offers[0].order
      ) {
        setValue("orderName", selectedSupplier.offers[0].order.name);
        setSelectedOrderId(selectedSupplier.offers[0].order.id);

        if (selectedSupplier.offers[0].order.startSupplyDate) {
          const startSupplyDate = new Date(
            selectedSupplier.offers[0].order.startSupplyDate
          );
          setValue("deliveryDate", startSupplyDate.toISOString().split("T")[0]);
        }
        if (selectedSupplier.offers[0].deliveryPlace) {
          setValue(
            "siteId",
            selectedSupplier.offers[0]?.order?.deliveryPlace?.id || ""
          );
        }
      }
    } else if (awardedSuppliers && awardedSuppliers.length > 0) {
      const emails = awardedSuppliers.map((supplier) => supplier.email);
      setValue("supplierEmails", emails);
    } else {
      setValue("supplierEmails", []);
    }
  }, [awardedSuppliers, setValue, selectedSupplier]);

  const { data: sites, isLoading: isSitesLoading } = useSites(
    user?.company?.id || ""
  );

  const { mutate: createDelivery, isPending: isCreatingDelivery } =
    useCreateDelivery(
      () => {
        if (isCreatingMultipleDeliveries) {
          const nextIndex = currentSupplierIndex + 1;
          if (awardedSuppliers && nextIndex < awardedSuppliers.length) {
            setCurrentSupplierIndex(nextIndex);
            createDeliveryForSupplier(awardedSuppliers[nextIndex]);
          } else {
            openNotification(i18next.t("deliveries.multipleSuccess"));
            setIsCreatingMultipleDeliveries(false);
            setCurrentSupplierIndex(0);
            navigate("/deliveries");
          }
        } else {
          openNotification(i18next.t("deliveries.success"));
          navigate("/deliveries");
        }
      },
      (error) => {
        openError(error);
        setOpen(false);
      }
    );

  const handleSpecification = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e?.target?.files?.[0];

    if (file) {
      const extension = file.name.split(".").pop()?.toLowerCase();

      if (!extension || !allowedExtensions.includes(extension)) {
        setFileError(i18next.t("deliveries.form.validations.extensions"));
        setOpen(false);
        return;
      }

      uploadFile(file);
      setFileName(file.name);
      setFileError("");
    }
  };

  const createDeliveryForSupplier = (supplier: UserResponse) => {
    const data = getValues();

    const allEmails = data.supplierEmails || [];
    if (!allEmails.includes(supplier.email)) {
      allEmails.push(supplier.email);
    }

    createDelivery(
      buildDeliveryPayload({
        data,
        user,
        uploadedSpecificationFileUrl,
        deliveryPlaceId: data.siteId,
        selectedOrderId,
        supplierId: supplier.id,
        supplierEmails: allEmails,
      })
    );
  };

  const onSubmit = (data: NewDeliveryFormValues) => {
    if (selectedSupplier) {
      createDelivery(
        buildDeliveryPayload({
          data,
          user,
          uploadedSpecificationFileUrl,
          deliveryPlaceId: data.siteId,
          selectedOrderId,
          supplierId: selectedSupplier.supplier.id,
          supplierEmails: data.supplierEmails,
        })
      );
    } else if (
      awardedSuppliers &&
      awardedSuppliers.length > 1 &&
      selectedOrderId
    ) {
      setIsCreatingMultipleDeliveries(true);
      setCurrentSupplierIndex(0);
      createDeliveryForSupplier(awardedSuppliers[0]);
    } else {
      createDelivery(
        buildDeliveryPayload({
          data,
          user,
          uploadedSpecificationFileUrl,
          deliveryPlaceId: data.siteId,
          selectedOrderId,
          supplierId:
            selectedOrderId && awardedSuppliers && awardedSuppliers.length > 0
              ? awardedSuppliers[0].id
              : "",
          supplierEmails: data.supplierEmails,
        })
      );
    }
  };

  const handleSupplierSelect = (supplier: OfferAwardedForDeliveryResponse) => {
    setSelectedSupplier(supplier);
    setShowForm(true);
    setValue("supplierEmails", [supplier.supplier.email]);

    if (
      supplier.offers &&
      supplier.offers.length > 0 &&
      supplier.offers[0].order
    ) {
      setValue("orderName", supplier.offers[0].order.name);
      setSelectedOrderId(supplier.offers[0].order.id);
      // Auto-populate delivery date from startSupplyDate, delivery place from the offer.
      if (supplier.offers[0].order.startSupplyDate) {
        const startSupplyDate = new Date(
          supplier.offers[0].order.startSupplyDate
        );
        setValue("deliveryDate", startSupplyDate.toISOString().split("T")[0]);
      }
      if (supplier.offers[0].deliveryPlace) {
        setValue("siteId", supplier.offers[0].deliveryPlace);
      }
    }
  };

  useEffect(() => {
    if (!selectedSupplier) {
      setValue("supplierEmails", []);
      trigger("supplierEmails");
    }
  }, [selectedSupplier, setValue, trigger]);

  if (
    isCreatingDelivery ||
    isSitesLoading ||
    isLoadingAwardedSuppliers ||
    isLoadingOffersAwardedForDeliveries
  )
    return <Loader isFullScreen />;

  const confirmBody = selectedSupplier
    ? t("deliveries.addSupplierQuestion", {
        supplier: selectedSupplier.supplier.companyName,
      })
    : awardedSuppliers && awardedSuppliers.length > 1 && selectedOrderId
      ? t("deliveries.addMultipleQuestion", {
          count: awardedSuppliers.length,
        })
      : t("deliveries.addQuestion");

  const showFormBody =
    showForm ||
    !offersAwardedForDeliveries ||
    offersAwardedForDeliveries.length === 0;

  return (
    <div>
      <PageHeader title={t("deliveries.new")} />

      {!showForm &&
        offersAwardedForDeliveries &&
        offersAwardedForDeliveries.length > 0 && (
          <DeliverySupplierPicker
            offersAwardedForDeliveries={offersAwardedForDeliveries}
            onSupplierSelect={handleSupplierSelect}
            onCreateNew={() => {
              setShowForm(true);
              setSelectedSupplier(null);
              setValue("orderName", "");
              setValue("supplierEmails", []);
              setSelectedOrderId("");
            }}
          />
        )}

      {showFormBody && (
        <>
          {selectedSupplier && (
            <div className="new-delivery-form__notice">
              <span>
                {t("deliveries.creatingForSupplier", {
                  firstName: selectedSupplier.supplier.firstName,
                  lastName: selectedSupplier.supplier.lastName,
                  company: selectedSupplier.supplier.companyName,
                })}
              </span>
            </div>
          )}
          <Form
            handleSubmit={handleSubmit}
            onSubmit={onSubmit}
            noValidate
            autoComplete="off"
            className="new-delivery-form cds-form-surface"
          >
            <DeliveryFormFields
              control={control}
              register={register}
              setValue={setValue}
              errors={errors}
              sites={sites}
              awardedSuppliers={awardedSuppliers}
              selectedSupplier={selectedSupplier}
              selectedOrderId={selectedOrderId}
              onFileChange={handleSpecification}
              fileName={fileName}
              fileError={fileError}
              isUploading={isUploading}
            />

            <div className="new-delivery-form__actions">
              {showForm && (
                <Button
                  kind="secondary"
                  onClick={() => {
                    setShowForm(false);
                    setSelectedSupplier(null);
                  }}
                >
                  {t("cta.back")}
                </Button>
              )}
              <Button as={Link} to="/deliveries" kind="secondary">
                {t("cta.cancel")}
              </Button>
              <Button
                onClick={(e) => {
                  e.preventDefault();
                  setOpen(true);
                }}
                disabled={isCreatingMultipleDeliveries || isUploading}
              >
                {isCreatingMultipleDeliveries
                  ? `${t("cta.uploading")} ${currentSupplierIndex + 1}/${
                      awardedSuppliers?.length
                    }`
                  : t("deliveries.new")}
              </Button>
            </div>
          </Form>
        </>
      )}

      {/* Mounted only while open: Carbon's ComposedModal (which ConfirmDialog wraps) always
          renders its footer confirm button even when closed, so mounting it unconditionally would
          leave a stray primary button colliding with the page's "Schedule Delivery" trigger. */}
      {open && (
        <ConfirmDialog
          open={open}
          title={t("deliveries.new")}
          body={confirmBody}
          confirmLabel={t("deliveries.new")}
          onConfirm={handleSubmit(onSubmit)}
          onCancel={() => setOpen(false)}
          isPending={isCreatingMultipleDeliveries || isUploading}
        />
      )}
    </div>
  );
};

export default NewDeliveryForm;
